import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Input, Label } from 'semantic-ui-react'
import {
    AppTable,
    AppEditButton,
    AppRemoveButtonPopup,
    LoaderBar,
    FlexBox,
    FlexRowWrap,
} from 'components'
import { AppPrimaryButton } from 'elements'
import { DesktopMode, MobileMode } from 'layout'
import { useNotification } from 'hooks/toast'
import { useAuth } from 'context'
import { ActionPlanCategoryService } from 'utility'
import { ActionPlanCategoryFormModal } from './ActionPlanCategoryFormModal'
import {
    categoryMatchesSearch,
    getApiErrorMessage,
    isActionPlanCategoryAdmin,
    normalizeActionPlanCategoryList,
} from './utils'

/** Mobile card — same pattern as Company / Template Group settings lists. */
const CategoryCard = ({ item, onEdit, onDelete, isAdmin }) => (
    <Card fluid className="mb-3">
        <Card.Content>
            <Card.Header>{item.name}</Card.Header>
            <Card.Meta style={{ marginTop: 8 }}>
                <Label
                    basic
                    color={item.isActive ? 'green' : 'grey'}
                    size="tiny"
                >
                    {item.isActive ? 'Active' : 'Inactive'}
                </Label>
            </Card.Meta>
            {isAdmin && (
                <Card.Description>
                    <FlexRowWrap className="justify-content-end">
                        <AppEditButton
                            type="button"
                            onClick={() => onEdit(item)}
                        />
                        {item.isActive && (
                            <AppRemoveButtonPopup
                                onConfirm={() => onDelete(item)}
                            />
                        )}
                    </FlexRowWrap>
                </Card.Description>
            )}
        </Card.Content>
    </Card>
)

/**
 * Settings → Ticket Categories
 * Desktop: AppTable (Template List / Template Group pattern)
 * Mobile: Card UI via MobileMode
 */
export const ActionPlanCategoryList = () => {
    const { user } = useAuth()
    const isAdmin = isActionPlanCategoryAdmin(user)
    const { successToast, errorToast } = useNotification()
    const toastRef = useRef({ successToast, errorToast })
    toastRef.current = { successToast, errorToast }

    const [loading, setLoading] = useState(true)
    const [categories, setCategories] = useState([])
    const [search, setSearch] = useState('')
    const [editing, setEditing] = useState(null)
    const [createOpen, setCreateOpen] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await ActionPlanCategoryService.list()
            setCategories(normalizeActionPlanCategoryList(res))
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
            setCategories([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const filtered = useMemo(
        () => categories.filter(c => categoryMatchesSearch(c, search)),
        [categories, search],
    )

    const softDeleteCategory = useCallback(async category => {
        const categoryId = Number(category.id)
        try {
            await ActionPlanCategoryService.remove(categoryId)
        } catch (err) {
            if (err?.status !== 404) throw err
            await ActionPlanCategoryService.update(categoryId, {
                CategoryName: category.name,
                IsActive: false,
            })
        }
    }, [])

    const handleDelete = useCallback(
        async category => {
            try {
                await softDeleteCategory(category)
                successToast(
                    'Category deactivated — Status set to Inactive. Existing rules keep working.',
                )
                setCategories(prev =>
                    prev.map(c =>
                        Number(c.id) === Number(category.id)
                            ? { ...c, isActive: false }
                            : c,
                    ),
                )
            } catch (err) {
                errorToast(getApiErrorMessage(err))
            }
        },
        [softDeleteCategory, successToast, errorToast],
    )

    const handleSaved = saved => {
        if (saved?.softDeleted) {
            successToast(
                'Category deactivated — Status set to Inactive. Existing rules keep working.',
            )
            setCategories(prev =>
                prev.map(c =>
                    Number(c.id) === Number(saved.id)
                        ? { ...c, ...saved, isActive: false }
                        : c,
                ),
            )
            return
        }
        successToast(saved?.id ? 'Category saved' : 'Category created')
        load()
    }

    const emptyMessage = search
        ? 'No categories match your search.'
        : 'No categories found'

    // AppTable column widths — keep Actions compact (same API as Template List)
    const columns = useMemo(
        () => [
            {
                title: 'Name',
                key: 'name',
                width: '55%',
            },
            {
                title: 'Status',
                key: 'status',
                width: '120px',
                render: item => (
                    <Label
                        basic
                        color={item.isActive ? 'green' : 'grey'}
                        size="tiny"
                    >
                        {item.isActive ? 'Active' : 'Inactive'}
                    </Label>
                ),
            },
            ...(isAdmin
                ? [
                      {
                          title: 'Actions',
                          key: 'actions',
                          width: '96px',
                          render: item => (
                              <FlexBox
                                  gap={8}
                                  className="justify-content-center"
                              >
                                  <AppEditButton
                                      type="button"
                                      onClick={() => setEditing(item)}
                                  />
                                  {item.isActive && (
                                      <AppRemoveButtonPopup
                                          onConfirm={() => handleDelete(item)}
                                      />
                                  )}
                              </FlexBox>
                          ),
                      },
                  ]
                : []),
        ],
        [isAdmin, handleDelete],
    )

    return (
        <div>
            {loading && <LoaderBar content="Loading" />}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                }}
                className="mb-2"
            >
                <h3 style={{ margin: 0 }}>Ticket Categories</h3>
                {isAdmin && (
                    <AppPrimaryButton
                        primary
                        onClick={() => setCreateOpen(true)}
                    >
                        + New Category
                    </AppPrimaryButton>
                )}
            </div>
            <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 12 }}>
                Soft-delete sets Status to Inactive and hides the category from
                the rule-editor dropdown; existing rules keep working.
            </p>
            <div className="mb-3" style={{ maxWidth: 360 }}>
                <Input
                    fluid
                    icon="search"
                    placeholder="Search categories…"
                    value={search}
                    onChange={(e, { value }) => setSearch(value)}
                />
            </div>

            <DesktopMode>
                <AppTable
                    data={filtered}
                    columns={columns}
                    hidePagination
                    emptyMessage={emptyMessage}
                />
            </DesktopMode>

            <MobileMode>
                {filtered.length === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 120,
                            background: '#fafbfc',
                            borderRadius: 8,
                            border: '1px solid #eee',
                            margin: '16px 0',
                            fontSize: '1rem',
                            color: '#888',
                            fontWeight: 500,
                        }}
                    >
                        {emptyMessage}
                    </div>
                ) : (
                    filtered.map(item => (
                        <CategoryCard
                            key={item.id}
                            item={item}
                            isAdmin={isAdmin}
                            onEdit={setEditing}
                            onDelete={handleDelete}
                        />
                    ))
                )}
            </MobileMode>

            <ActionPlanCategoryFormModal
                open={createOpen}
                category={null}
                onClose={() => setCreateOpen(false)}
                onSaved={handleSaved}
            />
            <ActionPlanCategoryFormModal
                open={Boolean(editing)}
                category={editing}
                onClose={() => setEditing(null)}
                onSaved={handleSaved}
            />
        </div>
    )
}
